import { Outlet, useLocation, useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { Sidebar } from '../index';
import { MobileAppBanner } from '../MobileAppBanner/MobileAppBanner';

import { useAppDispatch, useAppSelector, useAppThunkDispatch } from '../../hooks';
import { fetchAnnouncements } from '../../store/announcementSlice';
import { changeBuilding, getProfile, logout, selectSecurity } from '../../store/securitySlice';
import { FullPageSpinner, PageSpinner } from '../Spinner/Spinner';
import routes from '../../constants/routes';
import { useEffect, useRef, useState } from 'react';
import { checkBuildingStatus } from '../../../Building/services/BuildingService';
import { Text } from '../Text/Text';
import { Col } from '../Layout/Col';
import { Row } from '../Layout/Row';
import { Button } from '../Button/Button';
import { ButtonLink } from '../Button/ButtonLink';
import { CONTACT_URL } from '../../constants/common';
import { t } from '../../i18n';

import { ModalLock } from '../Modal/ModalLock';

// Maximální doba čekání na dokončení inicializace budovy (stahování vlastníků z KN apod.).
// Po jejím překročení místo nekonečného loaderu ukážeme uživateli chybovou hlášku.
const BUILDING_INIT_TIMEOUT_MS = 60_000;

export const PrivateRoute: React.FC = () => {
  const location = useLocation();
  const dispatch = useAppThunkDispatch();
  const security = useAppSelector(selectSecurity);

  const [loadingStep, setLoadingStep] = useState(0);
  const [isFetchingData, setIsFetchingData] = useState(true);
  const [isFetchingFailed, setIsFetchingFailed] = useState(false);

  const buildingState = security.ui.buildingState;
  const buildingId = security.ui.buildingId;

  // Hluboký odkaz z e-mailu (zpráva, hlasování, závady, soubory…) nese `?building=<id>`, protože cesta
  // sama dům neurčuje a aplikace jinak pracuje s naposledy zvoleným domem — u účtu s více domy by
  // se zpráva hledala ve špatném domě (404 → nekonečný spinner). Dům proto přepneme ještě před
  // vykreslením obsahu; pokud k němu účet přístup nemá, ukážeme vysvětlení místo chyby.
  const [searchParams] = useSearchParams();
  const requestedBuildingId = searchParams.get('building');
  const isBuildingSwitchRequested =
    security.state === 'authenticated' && !!requestedBuildingId && requestedBuildingId !== buildingId;
  const requestedBuilding = isBuildingSwitchRequested
    ? (security.buildings?.find((building) => building.buildingId === requestedBuildingId) ?? null)
    : null;

  useEffect(() => {
    if (requestedBuilding) dispatch(changeBuilding(requestedBuilding));
  }, [dispatch, requestedBuilding]);

  const initStartedAtRef = useRef<number>(Date.now());

  // Při přepnutí budovy restartujeme polling i timeout (a schováme případnou chybu).
  useEffect(() => {
    initStartedAtRef.current = Date.now();
    setIsFetchingFailed(false);
    setIsFetchingData(true);
    setLoadingStep(0);
  }, [buildingId]);

  useEffect(() => {
    if (!isFetchingData || buildingState === 'ready' || !buildingId) return;

    checkBuildingStatus()
      .then((res) => {
        switch (res.state) {
          case 'ready':
            dispatch(getProfile());
            break;

          case 'initialization':
          case 'loading':
            // Inicializace se zasekla (např. nedostupný / pomalý katastr) — po timeoutu
            // přestaneme čekat a ukážeme chybovou hlášku místo nekonečného loaderu.
            if (Date.now() - initStartedAtRef.current > BUILDING_INIT_TIMEOUT_MS) {
              setIsFetchingFailed(true);
              break;
            }
            setIsFetchingData(false);
            setLoadingStep((prevValue) => prevValue + 1);
            setTimeout(() => setIsFetchingData(true), 5000);
            break;

          case 'error':
            setIsFetchingFailed(true);
            break;
        }
      })
      .catch(() => setIsFetchingFailed(true));
  }, [dispatch, isFetchingData, buildingState, buildingId]);

  useEffect(() => {
    if (security.token && security.state === 'notAuthenticated') {
      dispatch(getProfile());
    }
  }, [dispatch, security.token, security.state]);

  // In-app oznámení pro adminy — jednou po naběhnutí budovy (a znovu při přepnutí budovy)
  const isAdmin = security.ui.isAdmin;
  useEffect(() => {
    if (buildingState === 'ready' && isAdmin && buildingId) {
      dispatch(fetchAnnouncements(buildingId));
    }
  }, [dispatch, buildingState, isAdmin, buildingId]);

  if (security.state === 'authenticated') {
    // Odkaz do jiného domu: buď na něj přepínáme (chvilka spinneru), nebo k němu účet nemá přístup.
    if (isBuildingSwitchRequested) {
      return requestedBuilding ? <PageSpinner /> : <BuildingAccessDenied email={security.profile?.email} />;
    }

    // Chyba inicializace nebo timeout — přednost před loaderem (i když je stav stále "loading").
    if (buildingId && isFetchingFailed) {
      return <LoadingState fetchingFailed />;
    }

    if ((buildingId && buildingState === 'initialization') || (buildingId && buildingState === 'loading')) {
      return <LoadingState step={loadingStep} />;
    }

    if (buildingId && buildingState === 'error') {
      return <LoadingState fetchingFailed />;
    }

    if (buildingState === 'ready') {
      return (
        <div className="Page">
          <Sidebar />
          <div className="Main">
            <MobileAppBanner />
            <Outlet />
          </div>

          <ModalLock />
        </div>
      );
    }
  }

  if (security.state === 'pending') {
    return <PageSpinner />;
  }

  if (security.state === 'badCredentials' || !security.token) {
    return <Navigate to={routes.login} state={{ from: location }} />;
  }

  return null;
};

interface BuildingAccessDeniedProps {
  email?: string;
}

// Odkaz z e-mailu vede do domu, ke kterému přihlášený účet nemá přístup (typicky jiný účet
// v jednom prohlížeči). Nabídneme přihlášení jiným účtem — po něm se uživatel vrátí na stejný odkaz.
const BuildingAccessDenied: React.FC<BuildingAccessDeniedProps> = ({ email }) => {
  const namespace = 'App.BuildingAccessDenied';
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSwitchAccount = () => {
    dispatch(logout());
    navigate(routes.login, { state: { from: location }, replace: true });
  };

  return (
    <div className="OnboardingForm Flex FlexAlign--center" style={{ minHeight: '100vh' }}>
      <Row>
        <Col className="FlexColumn FlexAlign--center">
          <Text size="h1" className="mb-1" align="center">
            {t('heading', namespace)}
          </Text>
          <Text align="center" className="mb-3">
            {t('text', namespace, { email: email ?? '' })}
          </Text>
          <Button title={t('switchAccount', namespace)} onClick={handleSwitchAccount} className="mb-2" />
          <ButtonLink to={routes.root} variant="text" title={t('continue', namespace)} />
        </Col>
      </Row>
    </div>
  );
};

interface LoadingStateProps {
  step?: number;
  fetchingFailed?: boolean;
}

const LoadingState: React.FC<LoadingStateProps> = ({ step, fetchingFailed }) => {
  const namespace = 'Building.Status';

  if (fetchingFailed) {
    return (
      <div className="OnboardingForm Flex FlexAlign--center" style={{ minHeight: '100vh' }}>
        <Row>
          <Col className="FlexColumn FlexAlign--center">
            <Text size="h1" className="mb-1" align="center">
              {t('FetchingFailed.heading', namespace)}
            </Text>
            <Text align="center" className="mb-2">
              {t('FetchingFailed.subheading', namespace)}
            </Text>
            <ButtonLink
              external={true}
              to={CONTACT_URL}
              target="_blank"
              title={t('FetchingFailed.buttonTitle', namespace)}
            />
          </Col>
        </Row>
      </div>
    );
  }

  return <FullPageSpinner namespace="Building.Status.Loading" step={step ?? 0} />;
};

export default PrivateRoute;
